import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import TextField from '../../components/TextField'
import LabelMessage from '../../components/LabelMessage'
import FormLabel from '@material-ui/core/FormLabel'
import TextareaAutosize from '@material-ui/core/TextareaAutosize'
import { ErrorMessage, Form, Formik, getIn } from 'formik'
import {
  AttachmentListRespType,
  AttachmentType,
  CaseCommonDataType,
  CaseReportId,
  PartType,
  CaseTIRespType,
  LookupItemType,
} from '../../models'
import {
  amountFilter,
  isoDateStrToDateOrNull,
  dateToISO,
  auxAffectedPersonCode,
  getAffectedPersonCode,
} from '../../helpers'
import { useStyles } from '../../pages/styled'
import { Attachments, AttachmentsChangedEvent } from '../Attachments'
import {
  combineAttachmentsList,
  getAttachmentsFromAttachmentListOrEmpty,
} from '../../models/attachmentListFnc'
import { DatePicker } from '../DatePicker'
import * as yup from 'yup'
import { CurrencyPicker } from '../CurrencyPicker'
import { LookupItemPicker } from '../LookupItemPicker'
import ErrorSubmitBack from '../ErrorSubmitBack'
import { Ga4Form } from './Ga4Form'
import { RadioButtonsField } from '../RadioButtonsField'
import { amountReplace, emptyStrToUndefined, objectToLookup, stringToNumber } from '../../helpers/miscFunctions'
export interface TicketInputDataType {
  caseCommon?: CaseCommonDataType | null
  caseTI?: CaseTIRespType | null
}

export interface TicketOutputDataType {
  caseCommon: CaseCommonDataType
  caseTI: CaseTIRespType
}

export interface TicketInputParamsType {
  caseReportId: CaseReportId
  data: TicketInputDataType
  isPageConfirmInProgress: boolean
  errorMessage: string
  onPageContinue(value: TicketOutputDataType): void
  onPageBack(): void
  notifyDataChanged(isChanged: boolean): void
}

const TicketForm = (params: TicketInputParamsType) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const reasons: LookupItemType[] = t(`reasons`) as any
  const filteredReasons = reasons.filter(
    (reason: LookupItemType) =>
      reason.code === 'ILLNESS' ||
      reason.code === 'INJURY' ||
      reason.code === 'PANDEMIA' ||
      reason.code === 'DELAY' ||
      reason.code === 'PROPERTY' ||
      reason.code === 'PREGNANCY' ||
      reason.code === 'DEATH' ||
      reason.code === 'OTHER'
  )
  const handleBackButtonClicked = () => {
    params.onPageBack()
  }

  const handleFormikSubmit = (value: any) => {
    const modelValue: TicketOutputDataType =
      transformFormikValuesToOutput(value)
    params.onPageContinue(modelValue)
  }

  function transformFormikValuesToOutput(values: any): TicketOutputDataType {
    const caseCommonFromFormik: CaseCommonDataType = {
      occurredOn: dateToISO(values.occurredOn),
      description: values.description,
    }
    const editedAttachmentList: AttachmentListRespType[] = [
      {
        attachmentType: AttachmentType.PHOTO,
        attachments: values.attachmentsPHOTO,
      },
      {
        attachmentType: AttachmentType.SUPPORT,
        attachments: values.attachmentsSUPPORT,
      },
    ]

    const caseTIFromFormik: CaseTIRespType = {
      eventName: values.eventName,
      reason: values.reason,
      insuredRelation:
        values.affectedPerson !== auxAffectedPersonCode
          ? values.affectedPerson ?? undefined
          : values.relation ?? undefined,
      priceCur: stringToNumber(values.priceCur),
      currency: emptyStrToUndefined(values.currency),
      attachmentList: combineAttachmentsList(
        params?.data?.caseTI?.attachmentList,
        editedAttachmentList
      ),
    }
    return { caseCommon: caseCommonFromFormik, caseTI: caseTIFromFormik }
  }

  const transformInputToFormikValues = (input: TicketInputDataType) => {
    return {
      eventName: input.caseTI?.eventName ?? '',
      reason: input.caseTI?.reason ?? null,
      priceCur: (input.caseTI?.priceCur ?? '').toString(),
      currency: input.caseTI?.currency ?? 'PLN',
      occurredOn: isoDateStrToDateOrNull(input.caseCommon?.occurredOn),
      description: input.caseCommon?.description ?? '',
      affectedPerson: getAffectedPersonCode(input.caseTI?.insuredRelation),
      relation:
        input?.caseTI?.insuredRelation == null
          ? null
          : getAffectedPersonCode(input?.caseTI?.insuredRelation) ===
            auxAffectedPersonCode
            ? input?.caseTI?.insuredRelation
            : null,
      attachmentsSUPPORT: getAttachmentsFromAttachmentListOrEmpty(
        input.caseTI?.attachmentList,
        AttachmentType.SUPPORT
      ),
      attachmentsPHOTO: getAttachmentsFromAttachmentListOrEmpty(
        input.caseTI?.attachmentList,
        AttachmentType.PHOTO
      ),
    }
  }

  const TicketValidationSchema = () =>
    yup.object().shape({
      description: yup.string(),
      reason: yup.string().nullable().required(t('ticketForm.reason.required')),
      eventName: yup.string().required(t('ticketForm.event.required')),
      occurredOn: yup
        .date()
        .nullable()
        .required(t('dateMessage.required'))
        .min(new Date('1999-12-31'), t('dateMessage.err2'))
        .max(new Date(), t('dateMessage.err2'))
        .typeError(t('dateMessage.err1')),
      affectedPerson: yup
        .string()
        .nullable()
        .required(t('ticketForm.relation.required')),
      relation: yup
        .string()
        .nullable()
        .when('affectedPerson', {
          is: (affectedPerson: any) => affectedPerson === auxAffectedPersonCode,
          then: yup
            .string()
            .required(t('ticketForm.relation.required'))
            .nullable(),
        }),
      priceCur: yup
        .string()
        .required(t('ticketForm.ticketPrice.required'))
        .test('numberFormat',
          t('message.err'),
          (val: string | undefined) => {
            const _number = Number(val)
            return !isNaN(_number)
          }
        )
        .test(
          'moreThan0',
          t('amount.moreThan0'),
          (val: string | undefined) => {
            const _number = Number(val)
            return _number > 0
          }
        ),
      currency: yup.string().required(t('currency.required')),
      attachmentsPHOTO: yup.array().nullable().min(1, t('uploadFile.err1')),
      attachmentsSUPPORT: yup.array().nullable().min(1, t('uploadFile.err1')),
    })

  const formikInitialValues = transformInputToFormikValues(params.data)

  const formikRef = useRef(null)
  return (
    <Ga4Form formName={'LiabilityForm'} >
      <Formik
        initialValues={formikInitialValues}
        onSubmit={(values) => handleFormikSubmit(values)}
        validationSchema={TicketValidationSchema}
        enableReinitialize={true}
        innerRef={formikRef}
      >
        {({
          initialValues,
          values,
          errors,
          touched,
          setFieldValue,
          handleChange,
          handleBlur,
        }) => {
          return (
            <Form className={classes.root}>
              <Grid
                item
                sm={12}
                xl={12}
                xs={12}
                container
                direction="row"
                className={classes.gridMainForm}
              >
                <Grid
                  item
                  sm={9}
                  xl={9}
                  xs={12}
                  container
                  direction="row"
                  className={classes.gridWorkArea}
                >
                  <Grid
                    item
                    sm={12}
                    xs={12}
                    xl={12}
                    container
                    className={classes.gridResize}
                  >
                    <Typography className={classes.text} variant="h3">
                      {t('ticketForm.title')}
                    </Typography>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        xl={12}
                        className={classes.gridItem}
                      >
                        <Grid item xs={12} sm={12} xl={12}>
                          <TextField
                            name="eventName"
                            value={values.eventName}
                            label={t('ticketForm.event.label')}
                            placeholder={t('ticketForm.event.hint')}
                            required
                            inputProps={{
                              maxLength: 200,
                            }}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!(errors.eventName && touched.eventName)}
                            helperText={
                              errors.eventName && touched.eventName
                                ? errors.eventName
                                : null
                            }
                            className={classes.fieldSize}
                            InputProps={{ required: false }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={6} xs={6} sm={6}>
                        <DatePicker
                          name="occurredOn"
                          value={values.occurredOn}
                          label={t('ticketForm.cancellationDate.label')}
                          required
                          minDate={new Date('2000, 0, 1')}
                          onChange={(value) => {
                            setFieldValue('occurredOn', value)
                          }}
                          error={Boolean(errors.occurredOn && touched.occurredOn)}
                          helperText={
                            errors.occurredOn && touched.occurredOn
                              ? errors.occurredOn
                              : null
                          }
                        />
                      </Grid>
                      <Grid item xl={6} xs={6} sm={6}>
                        <LookupItemPicker
                          name={`reason`}
                          label={t('ticketForm.reason.label')}
                          lookupItems={filteredReasons}
                          selectedItemCode={values.reason ?? null}
                          required
                          onValueChanged={(newValue: string | null) => {
                            setFieldValue(`reason`, newValue)
                          }}
                          onBlur={handleBlur}
                          error={Boolean(errors.reason && touched.reason)}
                          helperText={
                            errors.reason && touched.reason ? errors.reason : null
                          }
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12} className={classes.row}>
                        <RadioButtonsField
                          aria-label={'affectedPerson'}
                          name={'affectedPerson'}
                          label={t('cancellationPg.affectedPerson.label')}
                          required
                          value={values.affectedPerson}
                          onChange={handleChange}
                          lookup={objectToLookup(t('ticketForm.affectedPerson.code', { returnObjects: true }))}
                          error={Boolean(errors.affectedPerson && touched.affectedPerson)}
                          helperText={errors.affectedPerson && touched.affectedPerson
                            ? errors.affectedPerson
                            : null}
                        />
                      </Grid>
                    </Grid>
                    {values.affectedPerson === auxAffectedPersonCode && (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        xl={12}
                        container
                        className={classes.gridItem}
                        spacing={2}
                      >
                        <Grid
                          item
                          xl={12}
                          xs={12}
                          sm={12}
                          className={classes.fieldSize}
                        >
                          <LookupItemPicker
                            name={`relation`}
                            label={t('ticketForm.relation.label')}
                            lookupItems={t<string, LookupItemType[]>(
                              `relations`,
                              {
                                defaultValue: [] /* returned if not found */,
                                returnObjects:
                                  true /* array of {code:string, name:string} */,
                              }
                            )}
                            selectedItemCode={values.relation ?? null}
                            onValueChanged={(newValue: string | null) => {
                              setFieldValue(`relation`, newValue)
                            }}
                            helperText={
                              errors.relation && touched.relation
                                ? errors.relation
                                : null
                            }
                            error={Boolean(errors.relation && touched.relation)}
                            required={true}
                            onBlur={handleBlur}
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        className={classes.gridResize}
                      >
                        <FormLabel className={classes.textAreaLabel}>
                          {t('ticketForm.description.label')}
                        </FormLabel>
                        <TextareaAutosize
                          aria-label="description"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          rowsMin={7}
                          rowsMax={7}
                          placeholder={t('ticketForm.description.hint')}
                          className={classes.textAreaClass}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        className={classes.gridResize}
                      >
                        <Attachments
                          name={'attachmentsSUPPORT'}
                          caseReportId={params.caseReportId}
                          labelText={t('uploadAttachments.CASETI.SUPPORTTI')}
                          attachmentType={AttachmentType.SUPPORT}
                          part={PartType.CASETI}
                          attachments={values.attachmentsSUPPORT}
                          onAttachmentsChanged={(
                            event: AttachmentsChangedEvent
                          ) => {
                            setFieldValue(
                              'attachmentsSUPPORT',
                              event.value.attachments
                            )
                          }}
                          onBlur={handleBlur}
                          required={true}
                          error={
                            getIn(errors, 'attachmentsSUPPORT') &&
                            getIn(touched, 'attachmentsSUPPORT')
                          }
                          helperText={
                            getIn(errors, 'attachmentsSUPPORT') &&
                              getIn(touched, 'attachmentsSUPPORT')
                              ? getIn(errors, 'attachmentsSUPPORT')
                              : null
                          }
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        xl={6}
                        xs={6}
                        sm={6}
                        className={classes.fieldSize}
                      >
                        <LabelMessage
                          name={`priceCur`}
                          touched={touched}
                          errors={errors}
                          text={t('ticketForm.ticketPrice.label')}
                        />
                        <TextField
                          regex={amountFilter}
                          // @ts-ignore
                          name={`priceCur`}
                          // @ts-ignore
                          value={values.priceCur}
                          placeholder={t('ticketForm.ticketPrice.hint')}
                          onChange={(e) => {
                            setFieldValue(
                              `priceCur`,
                              amountReplace(e.target.value) ?? ''
                            )
                          }}
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                          inputProps={{ maxLength: 12 }}
                          onBlur={handleBlur}
                          required
                        />
                        <ErrorMessage
                          name={`priceCur`}
                          component="div"
                          className={classes.checkError}
                        />
                      </Grid>
                      <Grid
                        item
                        xl={6}
                        xs={6}
                        sm={6}
                        className={classes.fieldSize}
                      >
                        <CurrencyPicker
                          name={`currency`}
                          label={t('currency.label')}
                          selectedItemCode={values.currency ?? null}
                          onValueChanged={(
                            selectedCurrencyCode: string | null
                          ) => {
                            setFieldValue(`currency`, selectedCurrencyCode)
                          }}
                          error={
                            getIn(errors, `currency`) &&
                            getIn(touched, `currency`)
                          }
                          helperText={
                            getIn(errors, `currency`) &&
                              getIn(touched, `currency`)
                              ? getIn(errors, `currency`)
                              : null
                          }
                          required={true}
                          onBlur={handleBlur}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        className={classes.gridResize}
                      >
                        <Attachments
                          name={'attachmentsPHOTO'}
                          caseReportId={params.caseReportId}
                          labelText={t('uploadAttachments.CASETI.PHOTO')}
                          attachmentType={AttachmentType.PHOTO}
                          part={PartType.CASETI}
                          attachments={values.attachmentsPHOTO}
                          onAttachmentsChanged={(
                            event: AttachmentsChangedEvent
                          ) => {
                            setFieldValue(
                              'attachmentsPHOTO',
                              event.value.attachments
                            )
                          }}
                          onBlur={handleBlur}
                          required={true}
                          error={
                            getIn(errors, 'attachmentsPHOTO') &&
                            getIn(touched, 'attachmentsPHOTO')
                          }
                          helperText={
                            getIn(errors, 'attachmentsPHOTO') &&
                              getIn(touched, 'attachmentsPHOTO')
                              ? getIn(errors, 'attachmentsPHOTO')
                              : null
                          }
                        />
                      </Grid>
                    </Grid>
                    <ErrorSubmitBack
                      formik={formikRef.current}
                      pending={false}
                      backLinkText={t('backBtn')}
                      onBackLinkClick={handleBackButtonClicked}
                    >
                      {t('continueBtn')}
                    </ErrorSubmitBack>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Ga4Form >
  )
}

export default TicketForm
